<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SelectKnowledgeBaseDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/SelectKnowledgeBaseDialog.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const knowledgeBaseId = computed(() => Number(route.params.id));
const knowledgeBase = ref(null);

// 知识库切换弹窗
const selectKbDialogRef = ref(null);

// 图片路径
import webThumbnail from 'assets/images/dashboard/pilot/document-light.svg';
import webThumbnailDark from 'assets/images/dashboard/pilot/document-dark.svg';

// 图片加载错误处理
const handleThumbnailError = event => {
  event.target.style.display = 'none';
};

// URL 模式状态
const urlList = ref([]); // 默认0条URL输入框
const crawlLinks = ref(false);
const MAX_URLS = 10;

// 通用状态
const isSubmitting = ref(false);

// 假数据（模拟已添加的URL）- 只显示URL
const mockUrls = [
  'https://www.example.com/product',
  'https://help.example.com/docs',
  'https://blog.example.com/articles',
];

// 获取知识库详情
const fetchKnowledgeBase = async () => {
  try {
    const response = await store.dispatch('pilotKnowledgeBases/show', {
      id: knowledgeBaseId.value,
    });
    knowledgeBase.value = response.payload;
  } catch (error) {
    console.error('Failed to fetch knowledge base:', error);
  }
};

// 监听路由参数变化，重新获取知识库数据
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      fetchKnowledgeBase();
    }
  }
);

// 打开知识库选择弹窗
const openSelectKbDialog = () => {
  selectKbDialogRef.value?.open();
};

// 切换知识库
const handleKnowledgeBaseChange = newKbId => {
  router.replace({
    name: 'pilot_documents_web_crawl',
    params: { id: newKbId },
  });
};

// URL 模式相关函数
const addUrl = () => {
  // 检查是否超过最大数量限制
  if (urlList.value.length >= MAX_URLS) {
    useAlert(
      t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_URL_LIMIT_ERROR', {
        max: MAX_URLS,
      })
    );
    return;
  }

  // 添加一个新的空URL输入框
  urlList.value.push({ id: Date.now(), value: '' });
};

const removeUrl = index => {
  urlList.value.splice(index, 1);
};

const updateUrl = (index, newValue) => {
  urlList.value[index].value = newValue;
};

// 判断是否有已输入的URL
const hasValidUrls = () => {
  return urlList.value.some(item => item.value.trim() !== '');
};

const validateUrls = () => {
  const urlPattern = /^https?:\/\/.+/i;
  const validUrls = [];
  const normalizedUrls = new Set();

  for (const urlItem of urlList.value) {
    const url = urlItem.value.trim();

    // 跳过空值
    if (!url) continue;

    // URL 格式验证
    if (!urlPattern.test(url)) {
      useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_URL_ERROR'));
      return null;
    }

    // 检查重复
    const normalizedUrl = url.replace(/\/$/, '');
    if (normalizedUrls.has(normalizedUrl)) {
      useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_URL_DUPLICATE_ERROR'));
      return null;
    }
    normalizedUrls.add(normalizedUrl);

    validUrls.push(url);
  }

  return validUrls;
};

// 返回列表页
const goBack = () => {
  router.push({
    name: 'pilot_knowledge_bases_documents',
    params: { id: knowledgeBaseId.value },
  });
};

// 提交处理
const handleSubmit = async () => {
  const validUrls = validateUrls();
  if (!validUrls || validUrls.length === 0) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_NO_URLS_ERROR'));
    return;
  }
  await submitUrls(validUrls);
};

const submitUrls = async validUrls => {
  isSubmitting.value = true;

  try {
    let successCount = 0;
    let errorCount = 0;

    for (const url of validUrls) {
      try {
        const formData = new FormData();
        formData.append('document[knowledge_base_id]', knowledgeBaseId.value);
        formData.append('document[external_link]', url);
        formData.append('document[crawl_links]', crawlLinks.value);

        await store.dispatch('pilotDocuments/create', formData);
        successCount++;
      } catch (error) {
        console.error('Failed to add URL:', error);
        errorCount++;
      }
    }

    if (successCount > 0) {
      useAlert(
        t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.UPLOAD_SUCCESS', {
          count: successCount,
        })
      );
    }

    if (errorCount > 0) {
      useAlert(
        t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.PARTIAL_ERROR', {
          count: errorCount,
        })
      );
    }

    // 返回列表页
    goBack();
  } catch (error) {
    console.error('Upload error:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.UPLOAD_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  fetchKnowledgeBase();
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-slate-white dark:bg-n-slate-800">
    <!-- Header -->
    <header class="sticky top-0 z-10 px-6 bg-n-slate-white dark:bg-n-slate-800">
      <div class="w-full mx-auto max-w-[60rem]">
        <div class="flex items-center h-20">
          <button
            type="button"
            class="p-2 -ml-2 text-n-slate-600 dark:text-n-slate-400 hover:bg-n-slate-100 dark:hover:bg-n-slate-700 rounded-lg transition-colors"
            @click="goBack"
          >
            <Icon icon="i-lucide-arrow-left" class="h-5 w-5" />
          </button>
          <h1
            class="ml-4 text-lg font-semibold text-n-slate-900 dark:text-n-slate-100"
          >
            {{ t('PILOT.KNOWLEDGE_BASES.SIDEBAR.CRAWL_WEBPAGE') }}
          </h1>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1 w-full px-6 overflow-y-auto">
      <div class="w-full mx-auto max-w-[60rem] py-6 flex flex-col gap-6">
        <!-- 虚线介绍区域 -->
        <section class="custom-dashed-border rounded-2xl py-5 px-6">
          <div
            class="flex flex-col md:flex-row items-start md:items-center gap-6"
          >
            <div
              class="flex-shrink-0 w-[7.5rem] h-[6.5rem] rounded-lg flex items-center justify-center overflow-hidden"
            >
              <img
                :src="webThumbnail"
                :alt="
                  t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WEB_CRAWL.GUIDE_TITLE')
                "
                class="w-full h-full object-cover rounded-lg block dark:hidden"
                @error="handleThumbnailError"
              />
              <img
                :src="webThumbnailDark"
                :alt="
                  t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WEB_CRAWL.GUIDE_TITLE')
                "
                class="w-full h-full object-cover rounded-lg hidden dark:block"
                @error="handleThumbnailError"
              />
            </div>
            <div class="flex flex-col flex-1 gap-3">
              <p class="text-n-slate-12 text-sm font-medium">
                {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WEB_CRAWL.GUIDE_TITLE') }}
              </p>
              <p class="text-n-slate-11 text-sm">
                {{
                  t(
                    'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WEB_CRAWL.GUIDE_DESCRIPTION'
                  )
                }}
              </p>
            </div>
          </div>
        </section>

        <!-- 知识库信息（锁定状态） -->
        <div
          class="w-full flex items-start justify-between gap-3 p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2 text-left"
        >
          <!-- Logo -->
          <div
            class="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
            :class="knowledgeBase?.logo_url ? '' : 'bg-n-brand/10'"
          >
            <img
              v-if="knowledgeBase?.logo_url"
              :src="knowledgeBase.logo_url"
              :alt="knowledgeBase?.name"
              class="w-full h-full object-cover"
            />
            <span v-else class="text-base font-semibold text-n-brand">
              {{ knowledgeBase?.name?.charAt(0)?.toUpperCase() || '?' }}
            </span>
          </div>

          <!-- 信息 -->
          <div class="flex flex-col min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <span class="text-sm text-n-slate-12 font-medium">{{
                knowledgeBase?.name || '加载中...'
              }}</span>
              <span
                v-if="knowledgeBase?.knowledge_base_type === 'faq'"
                class="px-2 py-0.5 text-xs rounded-full font-medium bg-n-emerald-100 text-n-emerald-700 dark:bg-n-emerald-900/30 dark:text-n-emerald-400"
              >
                FAQs
              </span>
            </div>
            <span class="text-sm text-n-slate-11 mt-2">
              {{
                knowledgeBase?.description ||
                t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.NO_DESCRIPTION')
              }}
            </span>
            <!-- 文档和FAQ数量 -->
            <div class="flex items-center gap-4 text-sm text-n-slate-11 mt-2">
              <div class="flex items-center gap-1.5">
                <span class="i-lucide-file-text size-3" />
                <span>{{ knowledgeBase?.documents_count || 0 }} 文档</span>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="i-lucide-message-circle-question size-3" />
                <span>{{ knowledgeBase?.faqs_count || 0 }} FAQ</span>
              </div>
            </div>
          </div>

          <!-- 切换按钮 -->
          <button
            type="button"
            class="flex items-center gap-1 text-sm text-n-slate-9 hover:text-n-brand flex-shrink-0 transition-colors"
            @click="openSelectKbDialog"
          >
            <span class="i-lucide-arrow-right-left size-4" />
            <span>{{ t('PILOT.KNOWLEDGE_BASES.SWITCH_KB') }}</span>
          </button>
        </div>

        <!-- URL 输入区域 -->
        <div v-if="urlList.length > 0" class="flex flex-col gap-3">
          <div
            v-for="(urlItem, index) in urlList"
            :key="urlItem.id"
            class="flex gap-3"
          >
            <input
              :value="urlItem.value"
              type="text"
              class="flex-1 px-4 py-2 border border-n-slate-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent dark:bg-n-slate-7 dark:border-n-slate-6 dark:text-n-slate-12 no-margin"
              :placeholder="
                t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_URL_PLACEHOLDER')
              "
              @input="updateUrl(index, $event.target.value)"
            />
            <Button
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              @click="removeUrl(index)"
            />
          </div>
        </div>

        <!-- 添加URL按钮 -->
        <Button
          v-if="urlList.length > 0 && urlList.length < MAX_URLS"
          :label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.ADD_URL_BUTTON')"
          icon="i-lucide-plus"
          slate
          @click="addUrl"
        />

        <!-- 爬取链接选项 -->
        <div v-if="urlList.length > 0" class="flex flex-col gap-1">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="crawlLinks"
              type="checkbox"
              class="w-4 h-4 rounded border-n-slate-3 text-n-brand focus:ring-n-brand"
            />
            <span
              class="text-sm font-medium text-n-slate-12 dark:text-n-slate-1"
            >
              {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_CRAWL_LABEL') }}
            </span>
          </label>
          <p class="ml-6 text-xs text-n-slate-11 dark:text-n-slate-5">
            {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_CRAWL_HINT') }}
          </p>
        </div>

        <!-- 假数据预览区域（仅当没有输入URL时显示） -->
        <div
          v-if="urlList.length === 0"
          class="relative overflow-hidden rounded-xl min-h-[320px]"
        >
          <!-- 假数据URL列表（半透明） -->
          <div class="w-full space-y-2 opacity-50 pointer-events-none p-4">
            <div
              v-for="(mockUrl, index) in mockUrls"
              :key="index"
              class="flex items-center p-3 bg-n-slate-1 dark:bg-n-slate-7 rounded-lg"
            >
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <Icon
                  icon="i-lucide-globe"
                  class="text-xl text-n-slate-7 flex-shrink-0"
                />
                <div class="flex-1 min-w-0">
                  <p class="text-sm text-n-slate-12 truncate">
                    {{ mockUrl }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- 渐变蒙层 + 标题/描述/按钮 -->
          <div
            class="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-transparent from-0% via-white/50 dark:via-n-slate-800/50 to-white dark:to-n-slate-800"
          >
            <div class="flex flex-col items-center justify-center gap-3">
              <h3 class="text-3xl font-medium text-center text-n-slate-12">
                {{
                  t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WEB_CRAWL.PREVIEW_TITLE')
                }}
              </h3>
              <p class="max-w-xl text-base text-center text-n-slate-11">
                {{
                  t(
                    'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WEB_CRAWL.PREVIEW_DESCRIPTION'
                  )
                }}
              </p>
              <Button
                :label="
                  t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WEB_CRAWL.ADD_URL_BUTTON')
                "
                icon="i-lucide-plus"
                blue
                @click="addUrl"
              />
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer
      class="sticky bottom-0 z-10 px-6 py-4 bg-n-slate-white dark:bg-n-slate-800 border-t border-n-weak"
    >
      <div class="w-full mx-auto max-w-[60rem]">
        <div class="flex gap-3 justify-end">
          <Button
            :label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.CANCEL')"
            variant="faded"
            color="slate"
            @click="goBack"
          />
          <Button
            :label="
              t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.SUBMIT_URLS_BUTTON', {
                count: urlList.length,
              })
            "
            :is-loading="isSubmitting"
            :disabled="isSubmitting || urlList.length === 0"
            blue
            @click="handleSubmit"
          />
        </div>
      </div>
    </footer>
  </div>

  <!-- 知识库选择弹窗 -->
  <SelectKnowledgeBaseDialog
    ref="selectKbDialogRef"
    action-type="switch"
    @select="handleKnowledgeBaseChange"
  />
</template>
